import type WebMap from '@arcgis/core/WebMap';
import { Box, Card, DismissableLayer, Flex, Slot } from '@volue/wave-react';
import React, { useCallback, useEffect } from 'react';

import useFeatureToggles from '@/common/hooks/feature-toggles';
import { useWebMap } from '@/features/execute-calculation-set/components/Calculation/GridElementsCalculationTacticResults/useFeatureDetails';
import { useAppState } from '@/overmind';
import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import { useHighlightGridElementInMap } from './useHighlightGridElementInMap';

type GroupedFeatures = {
  title: string;
  assetTypes: (string | number)[];
  features: FeatureWithIDs[];
};

function useAutoSelectOnSingleFeature(
  select: (selected: FeatureWithIDs) => void,
  featuresToPickFrom: FeaturesInBounds
) {
  useEffect(() => {
    if (featuresToPickFrom.features.length === 1) {
      select(featuresToPickFrom.features[0]);
    }
  }, [featuresToPickFrom]);
}

type SelectedFeaturesLocation = {
  x: number;
  y: number;
};
export type FeaturesInBounds = {
  features: FeatureWithIDs[];
};

export type FeaturesInBoundsWithLocation = FeaturesInBounds & {
  mapInteractionLocation: SelectedFeaturesLocation;
};

type Select = (newSelected: FeatureWithIDs) => void;

type FeaturePickerProps = {
  onSelected: Select;
  onClose: () => void;
  featuresToPickFrom: FeaturesInBoundsWithLocation;
};

export function FeaturePicker({
  onSelected,
  onClose,
  featuresToPickFrom
}: FeaturePickerProps) {
  const featureFlags = useFeatureToggles();

  const { highlight, stopHighlighting } = useHighlightGridElementInMap();
  const webMap = useWebMap();

  const [position, setPosition] = React.useState({
    x: featuresToPickFrom.mapInteractionLocation.x,
    y: featuresToPickFrom.mapInteractionLocation.y
  });
  const layerRef = React.useRef<HTMLDivElement | null>(null);

  const select = useCallback((selected: FeatureWithIDs) => {
    highlight(selected);
    onSelected(selected);
  }, []);

  useAutoSelectOnSingleFeature(select, featuresToPickFrom);

  const closePopup = () => {
    stopHighlighting();
    onClose();
    layerRef.current = null;
  };

  const subTypeGroups = useAppState().arcgis.featureTypes.value ?? {};

  const singleOrNoSelectedFeatures = featuresToPickFrom.features.length < 2;

  useEffect(() => {
    const layer = layerRef.current;
    if (layer) {
      const rect = layer.getBoundingClientRect();
      const shouldHeightBeAdjusted = rect.height + rect.y > window.innerHeight;
      const shouldWidthBeAdjusted = rect.width + rect.x > window.innerWidth;
      if (shouldWidthBeAdjusted) {
        setPosition({ ...position, x: position.x - rect.width });
      }
      if (shouldHeightBeAdjusted) {
        setPosition({ ...position, y: position.y - rect.height });
      }
      if (!shouldHeightBeAdjusted && !shouldWidthBeAdjusted) {
        setPosition(featuresToPickFrom.mapInteractionLocation);
      }
    }
  }, [featuresToPickFrom.mapInteractionLocation]);

  if (
    singleOrNoSelectedFeatures ||
    !featuresToPickFrom.mapInteractionLocation
  ) {
    return null;
  }

  if (webMap.type !== 'READY') return null;

  const availableGroups = mapToAvailableGroups(
    featuresToPickFrom.features,
    webMap.webMap
  );

  return (
    <span data-component={'execute-calsulation-set/' + FeaturePicker.name} style={{ display: 'contents' }}>
      <DismissableLayer
        ref={layerRef}
        as={Slot}
        onDismiss={() => closePopup()}
        css={{
          zIndex: 10000,
          position: 'absolute',
          left: `${position.x}px`,
          top: `${position.y}px`
        }}
      >
        <Card
          css={{
            maxWidth: '30%',
            maxHeight: '20rem',
            paddingBlock: 0
          }}
          className="epsilon"
        >
          <div id="features-widget" />
          <Flex
            flow="column"
            gap="spacingM"
            css={{
              overflow: 'auto',
              padding: '$spacingL'
            }}
          >
            {availableGroups.map(group => (
              <Flex flow="column" gap="spacingXs" key={group.title}>
                <Box css={{ color: '#939393', fontWeight: 600 }}>
                  {group.title}
                </Box>
                <Flex flow="column" gap="spacingS">
                  {group.features.map(feature => (
                    <Box
                      key={feature.gc.globalId}
                      onClick={e => {
                        e.stopPropagation();
                        onSelected({ ...feature });
                      }}
                      onMouseOver={e => {
                        e.stopPropagation();
                        highlight(feature);
                      }}
                      onMouseLeave={e => {
                        e.stopPropagation();
                        stopHighlighting();
                      }}
                      css={{
                        cursor: 'pointer',
                        paddingInline: '$spacingM',
                        '&:hover': {
                          backgroundColor: '$colorAccent10',
                          borderRadius: '$spacingXs'
                        }
                      }}
                    >
                      {getTitle(feature)}
                    </Box>
                  ))}
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Card>
      </DismissableLayer>
  </span>
  );

  function getTitle(feature: FeatureWithIDs): React.ReactNode {
    if (featureFlags.isShowingGridElementTitleEnabled())
      return (
        feature.gc.webMapTitle ??
        feature.gc.title ??
        feature.gc.globalId ??
        feature.gc.type ??
        'test'
      );
    return subTypeGroups[feature.gc.type as number];
  }
}

function mapToAvailableGroups(
  filteredFeatures: FeatureWithIDs[],
  webMap: WebMap
) {
  const allAvailableLayerGroups = toAllAvailableLayerGroups(webMap);
  const availableUnGroups = filteredFeatures.reduce<GroupedFeatures[]>(
    (availableGroups, feature) => {
      const group = allAvailableLayerGroups.find(group =>
        group.layerNames.includes(feature.gc.layerName)
      );
      const existingGroup = availableGroups.find(
        g => g.title === group?.groupName
      );
      if (existingGroup) {
        if (!isAlreadyAddedToGroup(existingGroup, feature)) {
          existingGroup.features.push(feature);
          existingGroup.assetTypes.push(feature.gc.type);
        }
      } else {
        availableGroups.push({
          title: group?.groupName ?? 'Others',
          assetTypes: [feature.gc.type],
          features: [feature]
        });
      }
      return availableGroups;
    },
    []
  );

  return availableUnGroups;

  function isAlreadyAddedToGroup(
    existingGroup: GroupedFeatures,
    feature: FeatureWithIDs
  ) {
    return existingGroup.features.find(
      f => f.gc.globalId === feature.gc.globalId
    );
  }

  function toAllAvailableLayerGroups(webMap: WebMap) {
    return webMap.allLayers.map(layer => {
      if (layer.type === 'subtype-group') {
        const groupLayer = layer as __esri.SubtypeGroupLayer;
        return {
          groupName: layer.title,
          layerNames: groupLayer.sublayers
            .map(sublayer => sublayer.title)
            .concat([layer.title])
        };
      }

      return {
        groupName: layer.title,
        layerNames: [layer.title]
      };
    });
  }
}
